/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import Link from '@react/react-spectrum/Link';
import {
    CONFIRMATION_BOX_BTN_IDS,
    DIALOG_REPO_KEYS,
} from './confirmationDialogConstants';
import CONFIRMATION_CTAS_REPO from './confirmationDialogActionsRepo';
import {
    AppDialogAction, AppDialogDescription, AppDialogInput,
} from '../../../../stores/dialog';
import { APP_DIALOG, UPDATE_ALL } from '../../../enums';
import TranslationUtil from '../../../translation';
import {
	 ACTION_ID,
} from '../appModelConstants';

const { INPUT_TYPE } = APP_DIALOG;

const getMessage = TranslationUtil.getMessage.bind(TranslationUtil);

const inputConfig = [
    { id: '0', type: INPUT_TYPE.CHECKBOX, label: 'ccx.confirmation.checkbox.importPreferences' },
    { id: '1', type: INPUT_TYPE.CHECKBOX, label: 'ccx.confirmation.checkbox.removeOld' },
];

const getInputList = (confirmationCheckboxes, onChangeOfOptions) => {
    if (!confirmationCheckboxes) {
        return [];
    }
    if (confirmationCheckboxes
        && !Array.isArray(confirmationCheckboxes)) {
        // eslint-disable-next-line no-param-reassign
        confirmationCheckboxes = [confirmationCheckboxes];
    }
    const confirmationCheckboxesById = confirmationCheckboxes.reduce((map, c) => {
        // eslint-disable-next-line no-param-reassign
        map[c.id] = c;
        return map;
    }, {});
    const inputList = [];

    inputConfig.forEach(({
        id, type, label,
    }) => {
        const localizeLabel = getMessage(label);
        const confirmationCheckbox = confirmationCheckboxesById[id];
        if (!confirmationCheckbox) {
            return;
        }
        inputList.push(new AppDialogInput({
            id: confirmationCheckbox.value,
            type,
            disabled: confirmationCheckbox.editable === 'false',
            value: confirmationCheckbox.selected === 'true',
            localizedText: localizeLabel,
            ariaLabel: localizeLabel,
            onChange: (value) => {
                confirmationCheckbox.selected = value ? 'true' : 'false';
                onChangeOfOptions();
            },
        }));
    });

    return inputList;
};

const getActionList = (confirmationDetails) => {
    const actionList = [];
    confirmationDetails.confirmationButtons.forEach((btnInConfirmationBox) => {
        if (!btnInConfirmationBox.id) {
            return;
        }
        switch (btnInConfirmationBox.id.toLowerCase()) {
        case CONFIRMATION_BOX_BTN_IDS.PRIMARY:
            actionList.push(new AppDialogAction({
                localizedText: getMessage('ccx.confirmation.btn.continue'),
                ...CONFIRMATION_CTAS_REPO[CONFIRMATION_BOX_BTN_IDS.PRIMARY],
            }));
            break;
        case CONFIRMATION_BOX_BTN_IDS.SECONDARY:
            actionList.push(new AppDialogAction({
                localizedText: getMessage('ccx.confirmation.btn.cancel'),
                ...CONFIRMATION_CTAS_REPO[CONFIRMATION_BOX_BTN_IDS.SECONDARY],
            }));
            break;
        default:
            break;
        }
    });
    return actionList;
};

class UpdateModelMapper {
    getDescription(appData, onAction) {
        const { confirmationDetails } = appData;
        if (confirmationDetails.confirmationCheckboxes) {
            return ([
                new AppDialogDescription(
                    APP_DIALOG.DESCRIPTION_TYPE.PARAGRAPH,
                    (
                        <div>
                            {getMessage('ccx.confirmation.update.desc')}
                            <Link
                                variant="quiet"
                                onClick={
                                    () => onAction(ACTION_ID.LEARN_MORE, DIALOG_REPO_KEYS.UPDATE)
                                }>
                                {TranslationUtil.getMessage('ccx.appAction.learnMore')}
                            </Link>
                        </div>
                    )
                ),
            ]);
        }
        return [];
    }

    updateDialogRepo(appData, onAction, onChangeOfOptions) {
        const { confirmationDetails, display_name: appName } = appData;
        const { confirmationCheckboxes } = confirmationDetails;
        return {
            heading: (appName === UPDATE_ALL ? getMessage('ccx.confirmation.update.title.allApps')
                : getMessage('ccx.confirmation.update.title', { appName })),
            description: this.getDescription(appData, onAction),
            actionList: getActionList(confirmationDetails),
            inputList: getInputList(confirmationCheckboxes, onChangeOfOptions),
        };
    }
}
export default new UpdateModelMapper();
